import React, { useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslate } from "helpers";

import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { Breadcrumb } from "ui/components/Breadcrumb";
import { Button } from "ui/components/Button/Button";
import Alert from "ui/components/Alert/Alert";

import { IntegrationSettingsPageCmp, IntegrationSettingsPageProps } from "./types";
import { NjuskaloSettingsCard } from "./components/NjuskaloSettingsCard";
import { MerSettingsCard } from "./components/MerSettingsCard";
import { EposlovanjeSettingsCard } from "./components/EposlovanjeSettingsCard";

import { Routes } from "../../navigation";
import { INTEGRATIONS, INITIAL_NJUSKALO_SETTINGS, INITIAL_MER_SETTINGS, INITIAL_EPOSLOVANJE_SETTINGS } from "../../consts";
import {
  IntegrationId,
  NjuskaloSettings,
  MerSettings,
  EposlovanjeSettings,
} from "../../types";
import { BreadcrumbItem } from "ui/components/Breadcrumb/types";
import { INTEGRATION_SETTINGS_PAGE_TITLE } from "ui/components/PageTitle/consts";


const IntegrationSettingsPage: IntegrationSettingsPageCmp = (
  props: IntegrationSettingsPageProps
) => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const { integrationId } = useParams<{ integrationId: string }>();

  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Find the integration
  const integration = useMemo(() => {
    return INTEGRATIONS.find((i) => i.id === integrationId);
  }, [integrationId]);

  // Initial settings based on integration type
  const getInitialSettings = useCallback(() => {
    switch (integrationId) {
      case IntegrationId.NJUSKALO:
        return INITIAL_NJUSKALO_SETTINGS;
      case IntegrationId.MER:
        return INITIAL_MER_SETTINGS;
      case IntegrationId.EPOSLOVANJE:
        return INITIAL_EPOSLOVANJE_SETTINGS;
      default:
        return {};
    }
  }, [integrationId]);

  // Handle save for Njuskalo
  const handleNjuskaloSave = useCallback(async (settings: NjuskaloSettings) => {
    setIsLoading(true);
    try {
      // TODO: Call API to save settings
      console.log("Saving Njuskalo settings:", settings);
      await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulate API call
      setSuccessMessage(t("admin.integration.settings.saved"));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error("Error saving Njuskalo settings:", error);
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  // Handle save for Mer
  const handleMerSave = useCallback(async (settings: MerSettings) => {
    setIsLoading(true);
    try {
      // TODO: Call API to save settings
      console.log("Saving Mer settings:", settings);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setSuccessMessage(t("admin.integration.settings.saved"));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error("Error saving Mer settings:", error);
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  // Handle save for Eposlovanje
  const handleEposlovanjeSave = useCallback(async (settings: EposlovanjeSettings) => {
    setIsLoading(true);
    try {
      // TODO: Call API to save settings
      console.log("Saving Eposlovanje settings:", settings);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setSuccessMessage(t("admin.integration.settings.saved"));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error("Error saving Eposlovanje settings:", error);
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  // Render the appropriate settings card
  const renderSettingsCard = () => {
    switch (integrationId) {
      case IntegrationId.NJUSKALO:
        return (
          <NjuskaloSettingsCard
            settings={INITIAL_NJUSKALO_SETTINGS}
            onSave={handleNjuskaloSave}
            isLoading={isLoading}
          />
        );
      case IntegrationId.MER:
        return (
          <MerSettingsCard
            settings={INITIAL_MER_SETTINGS}
            onSave={handleMerSave}
            isLoading={isLoading}
          />
        );
      case IntegrationId.EPOSLOVANJE:
        return (
          <EposlovanjeSettingsCard
            settings={INITIAL_EPOSLOVANJE_SETTINGS}
            onSave={handleEposlovanjeSave}
            isLoading={isLoading}
          />
        );
      default:
        return (
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <p className="text-slate-600">{t("admin.integration.settings.notfound")}</p>
          </div>
        );
    }
  };

  const handleBack = useCallback(() => {
    navigate("/integration/search");
  }, [navigate]);

  const breadcrumbs: BreadcrumbItem[] = [
    { text: "admin.sidebar.integration.search", route: Routes.IntegrationSearchPageRouter },
    { text: integration?.name || "admin.sidebar.integration.settings", route: "" },
  ];

  if (!integration) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <p className="text-slate-600">{t("admin.integration.notfound")}</p>
          <Button
            variant="secondary"
            size="small"
            onClick={handleBack}
            className="mt-4"
          >
            {t("admin.back")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="bg-white rounded-t-md border border-b mb-2">
        <PageHeader>
          <div className="flex items-center justify-between">
            <div>
              <PageTitle
                title={INTEGRATION_SETTINGS_PAGE_TITLE.text}
                icon={INTEGRATION_SETTINGS_PAGE_TITLE.icon}
                iconColor={INTEGRATION_SETTINGS_PAGE_TITLE.iconColor}
              />
              {/* Povratak ide kroz breadcrumb, bez zasebnog gumba. */}
              <Breadcrumb items={breadcrumbs} />
            </div>
          </div>
        </PageHeader>
      </div>

      <div className="px-4 py-4">
        {/* Success message */}
        {successMessage && (
          <div className="mb-4">
            <Alert variant="success" message={successMessage} />
          </div>
        )}

        {/* Settings card */}
        {renderSettingsCard()}
      </div>
    </div>
  );
};

IntegrationSettingsPage.route = Routes.IntegrationSettingsPageRouter;

export default IntegrationSettingsPage;
